import { useEffect, useMemo, useState } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useToast } from '@/lib/hooks/useToast'
import { useIndexedFoldersStore } from '@/lib/stores/indexedFoldersStore'
import { type IndexedFolderConfig } from '@/lib/api/indexedFoldersApi'
import { Loader2, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

const DISPLAY_ORDER_MIN = 0
const DISPLAY_ORDER_MAX = 999
const FOLDER_PATH_FORMAT = /^[^\s].*[/\\].*[^\s]$/

function extractFolderNameFromPath(folderPath: string | null | undefined): string {
  if (!folderPath) {
    return ''
  }

  const normalized = folderPath.trim().replace(/[/\\]+$/g, '')
  if (!normalized) {
    return ''
  }

  const segments = normalized.split(/[/\\]/).filter(Boolean)
  return segments.at(-1) ?? ''
}

function buildValidationSchema(t: (key: string) => string) {
  return z.object({
    folderPath: z
      .string()
      .trim()
      .min(1, t('admin.indexedFolders.folderPathDescription'))
      .regex(FOLDER_PATH_FORMAT, t('admin.indexedFolders.folderPathPlaceholder')),
    name: z.string().trim().min(1, t('admin.indexedFolders.namePlaceholder')),
    description: z.string().optional(),
    displayOrder: z
      .number()
      .int(t('admin.indexedFolders.displayOrderDescription'))
      .min(DISPLAY_ORDER_MIN, t('admin.indexedFolders.displayOrderDescription'))
      .max(DISPLAY_ORDER_MAX, t('admin.indexedFolders.displayOrderDescription')),
    isActive: z.boolean(),
  })
}

type FolderConfigFormFields = z.infer<ReturnType<typeof buildValidationSchema>>


interface FolderConfigFormProps {
  selectedFolder?: IndexedFolderConfig | null | undefined
}

export function FolderConfigForm({ selectedFolder }: FolderConfigFormProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [lastAutoName, setLastAutoName] = useState('')

  const { createFolder, updateFolder, deleteFolder, clearSelection, isLoading, folders, fetchFolders } = useIndexedFoldersStore()
  const safeFolders = useMemo(() => folders ?? [], [folders])

  const defaultDisplayOrder = useMemo(
    () => Math.min(safeFolders.length, DISPLAY_ORDER_MAX),
    [safeFolders.length]
  )

  const validationSchema = useMemo(() => buildValidationSchema(t), [t])
  const resolver: Resolver<FolderConfigFormFields> = zodResolver(validationSchema)

  const form = useForm<FolderConfigFormFields>({
    resolver,
    defaultValues: {
      folderPath: selectedFolder?.folderPath ?? '',
      name: selectedFolder?.name ?? '',
      description: selectedFolder?.description ?? '',
      displayOrder: selectedFolder?.displayOrder ?? defaultDisplayOrder,
      isActive: selectedFolder?.isActive ?? true,
    },
  })

  const pathSuggestions = useMemo(() => {
    const suggestions = safeFolders
      .map(folder => folder?.folderPath?.trim())
      .filter((path): path is string => Boolean(path))

    return [...new Set(suggestions)].slice(0, 8)
  }, [safeFolders])

  // Update form when selectedFolder changes
  useEffect(() => {
    const selectedFolderPath = selectedFolder?.folderPath ?? ''
    const selectedFolderName = selectedFolder?.name ?? ''

    if (selectedFolder) {
      form.reset({
        folderPath: selectedFolderPath,
        name: selectedFolderName,
        description: selectedFolder?.description ?? '',
        displayOrder: selectedFolder.displayOrder,
        isActive: selectedFolder.isActive,
      })
      setLastAutoName(selectedFolderName)
    } else {
      form.reset({
        folderPath: '',
        name: '',
        description: '',
        displayOrder: defaultDisplayOrder,
        isActive: true,
      })
      setLastAutoName('')
    }
  }, [selectedFolder, form, defaultDisplayOrder])

  // Auto-populate name from path when path changes
  const folderPathValue = form.watch('folderPath')
  const nameValue = form.watch('name')

  useEffect(() => {
    const extractedName = extractFolderNameFromPath(folderPathValue)

    if (extractedName && (!nameValue || nameValue === lastAutoName)) {
      form.setValue('name', extractedName, { shouldValidate: true })
      setLastAutoName(extractedName)
      return
    }

    if (!extractedName && nameValue === lastAutoName) {
      form.setValue('name', '', { shouldValidate: true })
      setLastAutoName('')
    }
  }, [folderPathValue, nameValue, form, lastAutoName])

  const resetToCreateMode = () => {
    form.reset({
      folderPath: '',
      name: '',
      description: '',
      displayOrder: defaultDisplayOrder,
      isActive: true,
    })
    setLastAutoName('')
  }

  const onSubmit = async (data: FolderConfigFormFields) => {
    setIsSubmitting(true)
    try {
      if (selectedFolder) {
        // Update mode
        await updateFolder(selectedFolder.id, {
          name: data.name.trim(),
          description: data.description || null,
          displayOrder: data.displayOrder,
          isActive: data.isActive,
        })
        toast({
          title: t('admin.indexedFolders.success'),
          description: t('admin.indexedFolders.folderUpdated'),
        })
        clearSelection()
      } else {
        // Create mode
        await createFolder({
          folderPath: data.folderPath.trim(),
          name: data.name.trim(),
          description: data.description || null,
          displayOrder: data.displayOrder,
          isActive: data.isActive,
        })
        toast({
          title: t('admin.indexedFolders.success'),
          description: t('admin.indexedFolders.folderCreated'),
        })
      }

      // Refetch folders from server to ensure consistency
      await fetchFolders()

      resetToCreateMode()
    } catch (error) {
      // On error, also refetch to show user what exists
      await fetchFolders().catch(() => {}) // ignore refetch errors

      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    resetToCreateMode()
    clearSelection()
  }

  const handleDelete = async () => {
    if (!selectedFolder) {
      return
    }

    setIsDeleting(true)
    try {
      await deleteFolder(selectedFolder.id)
      toast({
        title: t('admin.indexedFolders.success'),
        description: t('admin.indexedFolders.folderDeleted'),
      })

      clearSelection()
      resetToCreateMode()
      setIsDeleteConfirmOpen(false)
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.unknownError'),
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedFolder ? t('admin.indexedFolders.edit') : t('admin.indexedFolders.addNew')}
          </CardTitle>
          <CardDescription>
            {selectedFolder
              ? t('admin.indexedFolders.editDescription')
              : t('admin.indexedFolders.addNewDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Folder Path */}
              <FormField
                control={form.control}
                name="folderPath"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.indexedFolders.folderPath')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        list="indexed-folders-path-suggestions"
                        placeholder={t('admin.indexedFolders.folderPathPlaceholder')}
                        disabled={!!selectedFolder || isSubmitting || isLoading}
                        autoFocus={!selectedFolder}
                      />
                    </FormControl>
                    {pathSuggestions.length > 0 && (
                      <datalist id="indexed-folders-path-suggestions">
                        {pathSuggestions.map(path => (
                          <option key={path} value={path} />
                        ))}
                      </datalist>
                    )}
                    <FormDescription>
                      {t('admin.indexedFolders.folderPathDescription')} {t('admin.indexedFolders.folderPathPlaceholder')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.indexedFolders.name')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('admin.indexedFolders.namePlaceholder')}
                        disabled={isSubmitting || isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.indexedFolders.descriptionLabel')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('admin.indexedFolders.descriptionPlaceholder')}
                        rows={3}
                        disabled={isSubmitting || isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>{t('admin.indexedFolders.descriptionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Display Order */}
              <FormField
                control={form.control}
                name="displayOrder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.indexedFolders.displayOrder')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={DISPLAY_ORDER_MIN}
                        max={DISPLAY_ORDER_MAX}
                        disabled={isSubmitting || isLoading}
                        value={Number.isFinite(field.value) ? field.value : ''}
                        onChange={event => {
                          const nextValue = event.target.value
                          field.onChange(
                            nextValue === ''
                              ? Number.NaN
                              : Number.parseInt(nextValue, 10)
                          )
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('admin.indexedFolders.displayOrderDescription')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Active Status */}
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel>{t('admin.indexedFolders.isActive')}</FormLabel>
                      <FormDescription>{t('admin.indexedFolders.isActiveDescription')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting || isLoading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="flex items-center gap-2 min-h-11"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                  {selectedFolder ? t('admin.indexedFolders.update') : t('admin.indexedFolders.create')}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting || isLoading}
                  className="min-h-11"
                >
                  {t('admin.indexedFolders.cancel')}
                </Button>

                {selectedFolder && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive border-destructive/40 hover:text-destructive min-h-11"
                    disabled={isSubmitting || isLoading || isDeleting}
                    onClick={() => setIsDeleteConfirmOpen(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t('admin.indexedFolders.delete')}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.indexedFolders.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.indexedFolders.deleteConfirmDescription', {
                name: selectedFolder?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('common.deleting') : t('admin.indexedFolders.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
